import React from "react"
import { useTranslation } from "react-i18next"

import {
	operators,
	LECLERC_ORDER_TAGS_URL,
	GIANT_STORES_ORDER_TAGS_URL,
	EASY_CASH_ORDER_TAGS_URL,
	CASH_CONVERTER_ORDER_TAGS_URL,
	BICYCODE_ASSO_ORDER_TAGS_URL,
	PRO_ORDER_TAGS_DEFAULT_URL,
	YAMAHA_ORDER_TAGS_URL,
	HORS_AM_GENDER,
	AM_GENDER,
	PROAGGLO_ORDER_TAGS_URL,
	COLLECTIVITE_GENDER,
	VELOCISTE_GENDER,
	partnerChainList,
	BC_OPERATOR,
	FAQLinks,
} from "utils/constants"
import { privateRoutes } from "router/routes"
import FleetIcon from "components/Elements/Icons/Fleet"
import HomeIcon from "components/Elements/Icons/HomeIcon"
import { BicytrustIcon } from "components/Elements/Icons/BicytrustIcon"
import { useAuth } from "./useAuth"
import { useTheme } from "./useTheme"
import { hasAccessToTheBikeFleet } from "utils"
import { BikeIcon, TagIcon } from "components/Elements/Icons"
import TeamManagementIcon from "components/Elements/Icons/TeamManagement"
import MailIcon from "components/Elements/Icons/Mail"
import {
	BICYTRUST_CONTRACT_ID,
	BIKE_MANAGEMENT_ID,
	CONTACT_SUPPORT_ID,
	FLEET_MANAGEMENT_ID,
	ItemMenuType,
	LABEL_ORDER_ID,
	MENU_BO,
	TEAM_MANAGEMENT_ID,
} from "utils/constants/menuTypes"
import { Operator } from "models"
import { isFrenchLanguage } from "utils/i18n"
import { isAuthorizedAccessOrderLabel } from "utils/bikeLabel"

type AsideMenuContextType = {
	mainMenuCollection: ItemMenuType[]
	currentActiveAsideMenu: number
	previousActiveAsideMenu: number
	authorizedAccessOrderLabel: boolean
	getOrderTagsUrl: () => string
	setActiveAsideMenu: (value: number) => void
	findIndexMenu: (id) => number
	setPreviousActiveAsideMenu: (value: number) => void
	operatorsUser: Operator[]

	isArrowShowing: boolean
	isMenuCollapsed: boolean
	setIsArrowShowing: (value: boolean) => void
	setIsMenuCollapsed: (value: boolean) => void

	isCurrentActiveAsideMenuAvailableForOperator: boolean
}

const AsideMenuContext = React.createContext({} as AsideMenuContextType)

const useAsideMenu = () => React.useContext(AsideMenuContext)

const AsideMenuProvider = ({ children }: { children: React.ReactNode }) => {
	const { entity, user, isConnectedFromSupport, isAdmin, isUserDataCompleted, isBCOperator } =
		useAuth()
	const { currentTheme } = useTheme()
	const { t } = useTranslation()

	const currentStoreChain =
		entity?.entity_data?.est_une_chaine && entity?.entity_data?.code_chaine
	const operatorsUser: Operator[] = React.useMemo(() => {
		if (entity) {
			const allowedOperators = operators.filter((op) =>
				entity?.entity_data?.entity_permission?.operateurs.includes(op.id_api)
			)
			return allowedOperators
		}
		return []
	}, [entity])

	const [currentActiveAsideMenu, setCurrentActiveAsideMenu] = React.useState<number>(null)
	const [previousActiveAsideMenu, setPreviousActiveAsideMenu] = React.useState<number>(0)
	const [isArrowShowing, setIsArrowShowing] = React.useState<boolean>(false)
	const [isMenuCollapsed, setIsMenuCollapsed] = React.useState<boolean>(false)

	React.useEffect(() => {
		if (isUserDataCompleted)
			if (isAdmin && !isConnectedFromSupport) {
				setCurrentActiveAsideMenu(MENU_BO)
			} else setCurrentActiveAsideMenu(BIKE_MANAGEMENT_ID)
	}, [isUserDataCompleted, isConnectedFromSupport, isAdmin]) // eslint-disable-line react-hooks/exhaustive-deps

	const entityRole = entity?.entity_data?.entity_permission?.role
	const entityGender = entity?.gender
	const isMANAGEBIKES = entityRole === "MANAGEBIKES"
	const isBCTheme = currentTheme?.id_api === BC_OPERATOR
	const isAuthorizedBicytrust = (entity?.entity_data?.est_bicytrust && isBCTheme) || false

	const setActiveAsideMenu = (value: number) => {
		setPreviousActiveAsideMenu(currentActiveAsideMenu)
		setCurrentActiveAsideMenu(value)
	}

	const isAsso =
		entityGender === HORS_AM_GENDER ||
		entityGender === AM_GENDER ||
		entityGender === COLLECTIVITE_GENDER
	const isUserWithBCAsso =
		isMANAGEBIKES && isAsso && operatorsUser.some((operator) => operator.id_api === BC_OPERATOR)
	const isPro = entityGender === VELOCISTE_GENDER
	const isLeclercEntity =
		entity?.entity_data?.code_chaine ===
		partnerChainList.find((chain) => chain.value === "LECL")?.value
	const isEasyCashEntity =
		entity?.entity_data?.code_chaine ===
		partnerChainList.find((chain) => chain.value === "EACA")?.value
	const isCashConverterEntity =
		entity?.entity_data?.code_chaine ===
		partnerChainList.find((chain) => chain.value === "CACO")?.value
	const isGiantStoresConverterEntity =
		entity?.entity_data?.code_chaine ===
		partnerChainList.find((chain) => chain.value === "GIAN")?.value
	const isYamahaEntity =
		entity?.entity_data?.code_chaine ===
		partnerChainList.find((chain) => chain.value === "YAMA")?.value
	const isProAggloEntity =
		entity?.entity_data?.code_chaine ===
		partnerChainList.find((chain) => chain.value === "PAGL")?.value

	const getOrderTagsUrl = () => {
		if (isUserWithBCAsso) return BICYCODE_ASSO_ORDER_TAGS_URL
		if (isPro) {
			if (isLeclercEntity) return LECLERC_ORDER_TAGS_URL
			if (isEasyCashEntity) return EASY_CASH_ORDER_TAGS_URL
			if (isCashConverterEntity) return CASH_CONVERTER_ORDER_TAGS_URL
			if (isGiantStoresConverterEntity) return GIANT_STORES_ORDER_TAGS_URL
			if (isYamahaEntity) return YAMAHA_ORDER_TAGS_URL
			if (isProAggloEntity) return PROAGGLO_ORDER_TAGS_URL

			return PRO_ORDER_TAGS_DEFAULT_URL
		}
		return null
	}

	const isAvailableToOrderTags = isBCTheme
	const isAuthorizedToAccessBikeFleetPage = hasAccessToTheBikeFleet(
		entity?.data_operators,
		currentTheme?.id_api
	)

	const mainMenuCollection: ItemMenuType[] = React.useMemo(() => {
		return [
			{
				id: BIKE_MANAGEMENT_ID,
				label: "translation:menu:fnuci_register",
				url: privateRoutes.bike.path,
				icon: <BikeIcon size={28} />,
				isVisible: isMANAGEBIKES,
				dataCy: "search-bike",
				children: [
					{
						label: "translation:menu:activate_bike",
						url: privateRoutes.bike.basePath,
						external: false,
						isVisible: true,
						dataCy: "search-bike",
					},
					{
						label: "translation:menu:bike_collection_import",
						url: privateRoutes.bikeCollectionImport.path,
						external: false,
						isVisible: entity?.entity_data?.import_allowed,
						dataCy: "collection-import",
					},
					{
						label: "translation:menu:history",
						url: privateRoutes.history.path,
						external: false,
						isVisible: true,
						dataCy: "history",
					},
				],
			},

			{
				id: FLEET_MANAGEMENT_ID,
				label: "bike:bikeFleet:title",
				url: privateRoutes.bikeFleet.path,
				icon: <FleetIcon size={28} />,
				isVisible: isAuthorizedToAccessBikeFleetPage,
				dataCy: "bike-fleet",
				children: [
					{
						label: "bike:bikeFleet:title",
						url: privateRoutes.bikeFleet.path,
						external: false,
						isVisible: true,
						dataCy: "bike-fleet",
					},
				],
			},

			{
				id: BICYTRUST_CONTRACT_ID,
				label: "bike:insurance:list:title",
				icon: <BicytrustIcon size={28} className="" />,
				url: privateRoutes.bicytrust.path,
				isVisible: isAuthorizedBicytrust,
				dataCy: "bicytrust",
				children: [
					{
						label: "bike:insurance:list:title",
						url: privateRoutes.bicytrust.path,
						external: false,
						isVisible: true,
						dataCy: "bicytrust",
					},
				],
			},
			{
				id: TEAM_MANAGEMENT_ID,
				label: "translation:menu:store",
				icon: <TeamManagementIcon size={28} className="" />,
				url: privateRoutes.shop.path,
				isVisible: isMANAGEBIKES,
				dataCy: "shop-store",
				children: [
					{
						label: t("store.title", { ns: "partner" }),
						url: privateRoutes.shop.path,
						external: false,
						isVisible: true,
						dataCy: "shop-store",
					},
					{
						label: "translation:menu:manage_team",
						url: privateRoutes.team.path,
						external: false,
						isVisible: true,
						dataCy: "manage-team",
					},
				],
			},

			{
				id: CONTACT_SUPPORT_ID,
				label: "translation:menu:contact_support",
				icon: <MailIcon size={28} className="" />,
				url: privateRoutes.contact.path,
				isVisible: true,
				dataCy: "contact-support",
				children: [
					{
						label: t("title", { ns: "contact" }),
						url: privateRoutes.contact.path,
						external: false,
						isVisible: true,
						dataCy: "contact-support",
					},
					{
						label: t("menu.faq"),
						url: isFrenchLanguage() ? FAQLinks.FR : FAQLinks.EN,
						external: true,
						isVisible: true,
						dataCy: "faq",
					},
				],
			},

			{
				id: LABEL_ORDER_ID,
				label: "translation:menu:order_tags",
				icon: <TagIcon size={28} />,
				isVisible: isAvailableToOrderTags,
				dataCy: "order-tags",
				children: [
					{
						label: "translation:menu:order_tags",
						url: isAuthorizedAccessOrderLabel(isBCTheme, currentStoreChain)
							? privateRoutes.bikeLabelOrder.path
							: getOrderTagsUrl(),
						external: !isAuthorizedAccessOrderLabel(isBCTheme, currentStoreChain),
						isVisible: isBCOperator,
						dataCy: "order-tags",
					},

					// {
					// 	label: "translation:menu:billing",
					// 	url: "azerty",
					// 	external: false,
					// },
				],
			},
			{
				id: MENU_BO,
				label: "translation:menu:menu",
				icon: <HomeIcon size={28} className={""} />,
				isVisible: isAdmin,
				dataCy: "validate-partner",
				children: [
					{
						label: "translation:menu:manage_partners",
						url: privateRoutes.validatedPartner.path,
						external: false,
						isVisible: true,
						dataCy: "validate-partner",
					},
					{
						label: "translation:menu:manage_admin",
						url: privateRoutes.manageAdmin.path,
						dataCy: "bicycode-admin",
						external: false,
						isVisible: true,
					},
					{
						label: "translation:menu:search_bike",
						url: privateRoutes.bike.basePath,
						external: false,
						isVisible: true,
						dataCy: "search-bike",
					},
					{
						label: "translation:menu:search_user",
						url: privateRoutes.searchUser.path,
						external: false,
						isVisible: true,
						dataCy: "search-user",
					},
				],
			},
		]
	}, [user, entity, isAvailableToOrderTags, isAuthorizedToAccessBikeFleetPage, t]) // eslint-disable-line react-hooks/exhaustive-deps

	const isCurrentActiveAsideMenuAvailableForOperator = React.useMemo(() => {
		// Check if the current active aside menu is available for the operator
		return (
			currentActiveAsideMenu &&
			mainMenuCollection.some((menu) => menu.id === currentActiveAsideMenu && menu.isVisible)
		)
	}, [currentActiveAsideMenu, mainMenuCollection])

	const findIndexMenu = (id) => mainMenuCollection.findIndex((element) => element.id === id)
	const authorizedAccessOrderLabel = isAuthorizedAccessOrderLabel(isBCTheme, currentStoreChain)
	return (
		<AsideMenuContext.Provider
			value={{
				mainMenuCollection,
				currentActiveAsideMenu,
				setActiveAsideMenu,
				setPreviousActiveAsideMenu,
				previousActiveAsideMenu,
				findIndexMenu,
				operatorsUser,
				getOrderTagsUrl,
				authorizedAccessOrderLabel,
				isArrowShowing,
				isMenuCollapsed,
				setIsArrowShowing,
				setIsMenuCollapsed,

				isCurrentActiveAsideMenuAvailableForOperator,
			}}
		>
			{children}
		</AsideMenuContext.Provider>
	)
}

export { useAsideMenu, AsideMenuProvider }
